import React, { useState } from 'react';
import { Grid, Paper, Snackbar, Alert, Box, AppBar, Toolbar, Typography, GlobalStyles, TextField, Button } from '@mui/material';
import * as L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useDispatch, useSelector } from 'react-redux';

// Import our master hook
import { useDropPointManagement } from './hooks/useDropPointManagement';

// Import components
import MapView from './components/MapView/MapView';
import MapLegend from './components/MapLegend/MapLegend';
import LocationPanel from './components/LocationPanel';
import CellSelector from './components/CellSelector';
import { orderfulApi } from '../../services/api-instances';
import { getObstacles } from '../../actions/obstacleActions';
import { selectObstacles } from '../../reducers/obstaclesReducer';
import { getGeofences } from '../../actions/geofenceActions';
import { selectCellGeofences } from '../../reducers/geofencesReducer';

// Fix default markers for React Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Extract styles to avoid recreation on every render
const containerStyles = { height: '100vh', width: '100%', display: 'flex', flexDirection: 'column' as const };
const headerStyles = { minHeight: '64px', backgroundColor: '#fff', borderBottom: '1px solid #e0e0e0' };
const contentStyles = { flex: 1, height: 'calc(100vh - 64px)' };
const gridContainerStyles = { height: '100%' };
const mapSectionStyles = { height: '100%', width: '100%', position: 'relative' as const };
const paperStyles = { height: '100%', overflowY: 'auto' as const };

// Mounted-only styles, scoped to this view via a root class
const rootScope = '.drop-point-management';
const mountedGlobalStyles = {
  [`${rootScope} .circular-drop-point-marker`]: { zIndex: 500 },
  [`${rootScope} .circular-drop-point-marker.selected`]: { zIndex: 1000 },
  [`${rootScope} .circular-drop-point-marker.deleted`]: { zIndex: 200 },
  [`${rootScope} .building-tooltip`]: {
    background: 'rgba(0, 0, 0, 0.8) !important',
    color: 'white !important',
    border: 'none !important',
    borderRadius: 4,
    padding: 8,
    fontSize: 12,
    fontWeight: 500,
    whiteSpace: 'pre-line',
    maxWidth: 200,
  },
  [`${rootScope} .building-tooltip::before`]: {
    borderTopColor: 'rgba(0, 0, 0, 0.8) !important',
  },
  '@keyframes pulse': {
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0.5 },
  },
} as const;

const DropPointManagement: React.FC = () => {
  // Use our optimized master hook with performance monitoring and caching
  const { state, actions } = useDropPointManagement();
  const [eircodeInput, setEircodeInput] = useState<string>('');
  const [isSearching, setIsSearching] = useState<boolean>(false);

  const handleSearchByEircode = async () => {
    const raw = (eircodeInput || '').trim();
    if (!raw) return;
    try {
      setIsSearching(true);
      const eircode = raw.replace(/\s+/g, '').toUpperCase();
      const context = await orderfulApi.getPlaceContextByEircode(eircode);

      const lat = (context as any)?.place?.centroid?.latitude;
      const lng = (context as any)?.place?.centroid?.longitude;
      if (typeof lat === 'number' && typeof lng === 'number') {
        // Center map near the place and trigger places reload
        actions.handleMapMove(L.latLng(lat, lng), 17);
      }
      // Select the place to open the panel
      await actions.handlePlaceClick(context.place);
    } catch (err) {
      console.error('Eircode search failed', err);
    } finally {
      setIsSearching(false);
    }
  };
  const dispatch = useDispatch();
  const obstacles = useSelector(selectObstacles);
  const geofences = useSelector(selectCellGeofences);

  React.useEffect(() => {
    if (state.selectedCellId) {
      // Use combined endpoint where allowed
      dispatch(getObstacles(state.selectedCellId, true) as any);
    }
  }, [dispatch, state.selectedCellId]);

  React.useEffect(() => {
    dispatch(getGeofences() as any);
  }, [dispatch]);

  return (
    <div className="drop-point-management" style={containerStyles}>
      {/* Mounted-only global styles for this view */}
      <GlobalStyles styles={mountedGlobalStyles} />
      
      {/* Header with Cell Selector */}
      <Box sx={headerStyles}>
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Delivery Spot Management
          </Typography>
          <CellSelector
            cells={state.cells || []}
            selectedCellId={state.selectedCellId}
            onCellChange={actions.handleCellChange}
          />
          <Box sx={{ ml: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              size="small"
              label="Search by Eircode"
              value={eircodeInput}
              onChange={(e) => setEircodeInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSearchByEircode();
              }}
            />
            <Button
              variant="contained"
              size="small"
              onClick={handleSearchByEircode}
              disabled={!eircodeInput.trim() || isSearching}
            >
              {isSearching ? 'Searching…' : 'Search'}
            </Button>
          </Box>
        </Toolbar>
      </Box>
      
      {/* Main Content */}
      <div style={contentStyles}>
        <Grid container style={gridContainerStyles}>
        {/* Map Section - Responsive grid */}
        <Grid item xs={state.hasPlaceSelected ? 8 : 12} style={{ height: '100%' }}>
          <div style={mapSectionStyles}>
            <MapView
              mapCenter={state.mapCenter}
              mapZoom={state.mapZoom}
              places={state.places}
              dropPoints={state.dropPoints}
              geofences={geofences as any}
              selectedPlaceId={state.selectedPlaceId}
              selectedDropPointId={state.selectedDropPointId}
              preferredDropPointId={(state.selectedPlaceContext?.place as any)?.preferredDropPointId}
              placeStatuses={state.placeStatuses}
              placesUpdateKey={state.placesUpdateKey}
              clickMode={state.clickMode}
              loading={state.placesLoading}
              newPinLocation={state.newPinLocation}
              riskAssessment={state.riskAssessment}
              isLoadingRiskAssessment={state.isLoadingRiskAssessment}
              obstacles={obstacles}
              onPlaceClick={actions.handlePlaceClick}
              onDropPointClick={actions.handleDropPointClick}
              onMapClick={actions.handleMapClick}
              onMapMove={actions.handleMapMove}
              onZoom={actions.handleMapZoom}
              onPinMove={actions.handlePinMove}
            />
            
            <MapLegend
              placesCount={state.places.length}
              placeStatusesCount={state.placeStatuses.size}
              loading={state.placesLoading}
            />
          </div>
        </Grid>
        
        {/* Location Panel - Only rendered when needed */}
        {state.selectedPlaceContext && (
          <Grid item xs={4} style={{ height: '100%' }}>
            <Paper style={paperStyles}>
              <LocationPanel
                locationContext={state.selectedPlaceContext}
                riskAssessment={state.riskAssessment}
                isLoadingRiskAssessment={state.isLoadingRiskAssessment}
                newPinLocation={state.newPinLocation}
                clickMode={state.clickMode}
                selectedDropPointId={state.selectedDropPointId}
                onAddPin={actions.handleAddPin}
                onSavePin={actions.handleSavePin}
                onCancelPin={actions.handleCancelNewPin}
                onDropPointSelect={(dropPointId: string | null) => {
                  if (dropPointId) {
                    const dropPoint = state.dropPoints.find(dp => dp.id === dropPointId);
                    if (dropPoint) {
                      actions.handleDropPointSelect(dropPoint);
                    }
                  } else {
                    actions.handleDropPointSelect(null);
                  }
                }}
                onDropPointDelete={actions.handleDropPointDelete}
                onDropPointEvaluate={actions.handleDropPointEvaluate}
                onDropPointSetPreferred={actions.handleDropPointSetPreferred}
                onNoteCreated={actions.handleNoteCreated}
                onPlaceStatusUpdate={actions.handlePlaceStatusUpdate}
                onClose={actions.handleClose}
              />
            </Paper>
          </Grid>
        )}
        </Grid>
      </div>
      
      {/* Optimized Notifications */}
      <Snackbar
        open={state.notification.open}
        autoHideDuration={state.notification.autoHideDuration}
        onClose={actions.hideNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={actions.hideNotification} severity={state.notification.severity}>
          {state.notification.message}
        </Alert>
      </Snackbar>

      {/* Performance debug panel (development only) */}
      {process.env.NODE_ENV === 'development' && (
        <div 
          style={{
            position: 'fixed',
            top: '100px',
            right: '16px',
            backgroundColor: 'rgba(0,0,0,0.8)',
            color: 'white',
            padding: '12px',
            borderRadius: '8px',
            fontSize: '11px',
            fontFamily: 'monospace',
            zIndex: 2000,
            maxWidth: '250px',
          }}
        >
          <div><strong>Performance Monitor</strong></div>
          <div>Cache Hit Rate: {actions.getPerformanceMetrics().cacheHitRate.toFixed(1)}%</div>
          <div>Places Cached: {actions.getPerformanceMetrics().cacheStats.places.valid}</div>
          <div>Context Cached: {actions.getPerformanceMetrics().cacheStats.context.valid}</div>
          <button 
            onClick={actions.clearCache}
            style={{ 
              marginTop: '8px', 
              padding: '4px 8px', 
              backgroundColor: '#333', 
              color: 'white',
              border: '1px solid #666',
              borderRadius: '4px',
              fontSize: '10px'
            }}
          >
            Clear Cache
          </button>
        </div>
      )}
    </div>
  );
};

export default DropPointManagement;
